import { parseSearchIntent } from '~/utils/parseSearchIntent'
import type { SearchCity, SearchCitySource } from '~/utils/searchCity'
import type { FederatedSearchAnalyticsPlacement } from '~/utils/searchEngineHelpers'

/**
 * GA4 search analytics (KFT-4280): the params shared by `search`,
 * `view_search_results`, `select_content` and the search panel/city events.
 *
 * Every param is always sent ('' when it doesn't apply): GTM keeps the last
 * pushed value of a data layer key, so a param left out would report the
 * previous event's value.
 */

/** Which search box: hero, navbar, or none (a /search URL opened directly). */
export type SearchPlacementParam =
  | FederatedSearchAnalyticsPlacement
  | 'direct'
  | ''

/** `query` = a city named in the query overrides the city line. */
export type SearchCitySourceParam = SearchCitySource | 'query' | ''

export interface SearchAnalyticsParams {
  search_placement: SearchPlacementParam
  /** Slug of the city the search is scoped to ('' = not scoped / no city line). */
  search_city: string
  search_city_source: SearchCitySourceParam
  /** GA4 built-in content group of the page the search happened on. */
  content_group: string
}

export const withSearchAnalyticsDefaults = (
  params: Partial<SearchAnalyticsParams> = {}
): SearchAnalyticsParams => ({
  search_placement: params.search_placement ?? '',
  search_city: params.search_city ?? '',
  search_city_source: params.search_city_source ?? '',
  content_group: params.content_group ?? '',
})

/**
 * The city a hero search is scoped to: a city named in the query wins over the
 * city line (KFT-4205). Without the city line (navbar) nothing is scoped.
 */
export const buildSearchCityParams = ({
  withCity,
  city,
  source,
  queryCity,
}: {
  withCity: boolean
  city: SearchCity | null | undefined
  source: SearchCitySource
  queryCity?: SearchCity | null
}): Pick<SearchAnalyticsParams, 'search_city' | 'search_city_source'> => {
  if (!withCity) return { search_city: '', search_city_source: '' }
  if (queryCity)
    return { search_city: queryCity.slug, search_city_source: 'query' }
  return { search_city: city?.slug ?? '', search_city_source: source }
}

const cityNames = ({ name, slug }: SearchCity): string[] =>
  [
    ...(typeof name === 'string' ? [name] : Object.values(name ?? {})),
    slug,
  ].filter((value): value is string => typeof value === 'string' && !!value)

const escapeRegExp = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Whole words only, like parseSearchIntent's phrases (`\b` doesn't treat
// umlauts as word characters): "Romantik Rom" keeps "Romantik".
const wordPattern = (word: string) =>
  new RegExp(`(?:^|\\s)${escapeRegExp(word)}(?=\\s|$)`, 'gi')

/**
 * Coarse query type for GA4 `search_intent`, comma-joined: `city` (the query
 * names a city), `price` / `date` (constraint phrases, see parseSearchIntent),
 * `free_text` (anything left once those are removed). '' for an empty query.
 * `category` is not detected yet (KFT-4283).
 */
export const buildSearchIntent = (
  query: string,
  queryCity?: SearchCity | null,
  now: Date = new Date()
): string => {
  const term = query.trim()
  if (!term) return ''
  const { text, priceMax, dates } = parseSearchIntent(term, now)
  let rest = text
  if (queryCity) {
    for (const name of cityNames(queryCity)) {
      rest = rest.replace(wordPattern(name), ' ')
    }
  }
  return [
    queryCity && 'city',
    priceMax != null && 'price',
    dates && 'date',
    rest.trim() && 'free_text',
  ]
    .filter(Boolean)
    .join(',')
}

// ─── hand-off to the page a search leads to ──────────────────────────────────

export const SEARCH_ANALYTICS_HANDOFF_KEY = 'kft-search-analytics-handoff'

/**
 * Written by the federated search right before it navigates, read by the list
 * page it lands on (FilteredProductShowcase):
 * - `submit` (Enter / submit / show all → /search): that page sends
 *   `view_search_results` with this term and placement.
 * - `suggestion` (location / category row): the `search` event was already
 *   sent; the term only attributes clicks on that page (`item_search_term`).
 */
export interface SearchAnalyticsHandoff {
  kind: 'submit' | 'suggestion'
  term: string
  placement: FederatedSearchAnalyticsPlacement | ''
  city: string
  citySource: SearchCitySourceParam
  /** `search_intent` as the dropdown saw it (it knows a city named in the query). */
  intent?: string
}

/** The page a hand-off is meant for: its path and its `query` URL param. */
export interface SearchAnalyticsHandoffTarget {
  path: string
  query: string
}

interface StoredSearchAnalyticsHandoff {
  handoff: SearchAnalyticsHandoff
  target: SearchAnalyticsHandoffTarget
  createdAt: number
}

/** A hand-off the expected page never read goes stale after this. */
export const SEARCH_ANALYTICS_HANDOFF_TTL_MS = 60_000

// Path only, without the query string and trailing slash (the router is
// strict about trailing slashes, the hand-off writers aren't).
const normalizeTarget = ({
  path,
  query,
}: SearchAnalyticsHandoffTarget): SearchAnalyticsHandoffTarget => ({
  path: (path.split('?')[0] ?? '').replace(/\/+$/, ''),
  query: query.trim(),
})

export const setSearchAnalyticsHandoff = (
  handoff: SearchAnalyticsHandoff,
  target: SearchAnalyticsHandoffTarget,
  now: number = Date.now()
) => {
  const stored: StoredSearchAnalyticsHandoff = {
    handoff,
    target: normalizeTarget(target),
    createdAt: now,
  }
  try {
    sessionStorage.setItem(SEARCH_ANALYTICS_HANDOFF_KEY, JSON.stringify(stored))
  } catch {
    // Storage blocked: the landing page just reports `direct`.
  }
}

const isHandoff = (value: any): value is SearchAnalyticsHandoff =>
  (value?.kind === 'submit' || value?.kind === 'suggestion') &&
  typeof value.term === 'string' &&
  typeof value.placement === 'string' &&
  typeof value.city === 'string' &&
  typeof value.citySource === 'string' &&
  (value.intent === undefined || typeof value.intent === 'string')

const isStoredHandoff = (value: any): value is StoredSearchAnalyticsHandoff =>
  isHandoff(value?.handoff) &&
  typeof value.target?.path === 'string' &&
  typeof value.target?.query === 'string' &&
  typeof value.createdAt === 'number'

const clearHandoff = () => {
  try {
    sessionStorage.removeItem(SEARCH_ANALYTICS_HANDOFF_KEY)
  } catch {
    // Storage blocked: nothing was stored either.
  }
}

/**
 * The pending hand-off, if it was written for `landing` (same path and
 * `query` param) and is fresh; it's then cleared, so it counts once. One meant
 * for another page is kept for that page (it may still be loading); a stale
 * or malformed one is dropped, so a later visit can't inherit it.
 */
export const consumeSearchAnalyticsHandoff = (
  landing: SearchAnalyticsHandoffTarget,
  now: number = Date.now()
): SearchAnalyticsHandoff | null => {
  let stored: unknown
  try {
    const raw = sessionStorage.getItem(SEARCH_ANALYTICS_HANDOFF_KEY)
    if (!raw) return null
    stored = JSON.parse(raw)
  } catch {
    clearHandoff()
    return null
  }
  if (
    !isStoredHandoff(stored) ||
    now - stored.createdAt > SEARCH_ANALYTICS_HANDOFF_TTL_MS
  ) {
    clearHandoff()
    return null
  }
  const { path, query } = normalizeTarget(landing)
  if (stored.target.path !== path || stored.target.query !== query) return null
  clearHandoff()
  return stored.handoff
}
